import { Dispatch, FC, useMemo } from 'react';
import { AnyAction } from 'redux';
import { useDispatch, useSelector } from 'react-redux';

import { PlannerSecondaryPanelType } from 'store/dealManagement/types';
import { changeNewProductField, clearItemInNewProduct, hideSecondaryPanel } from 'store/productManagement/reducer';
import { useOnChangePanelVisibility } from 'components/pages/PCM/hooks/useOnChangePanelVisibility';
import routeFrameListSvg from 'assets/icons/route_frame_lists.svg';
import SectionButton, { SectionButtonTheme } from 'lib/SectionButton';
import { SectionSelectedType } from 'lib/SectionButton/SectionButton.types';

import { Store } from 'components/common/types/Store.types';
import { ProductFieldName, SelectionPermission } from 'store/productManagement/reducer.types';
import { generateRouteFramesSelectedDisplay } from 'components/pages/Planner/PlannerSections/FiltersContent/FrameList/utils/generateRouteFramesSelectedDisplay';
import { sortByCategoryIncludeExclude } from 'components/pages/Planner/PlannerSections/FiltersContent/utils';
import { RouteFrameCode } from 'components/common/types/Deal.types';
import { FileList, CodeNameModel } from 'components/common/types';
import { useFrameListExportActions } from 'components/pages/Planner/PlannerSections/FiltersContent/FrameList/useFrameListExportActions';
import { getIsReadOnly } from 'store/productManagement/selectors';
import ProductFrameListPanel from './ProductFrameListPanel';
import { isValueRequired } from '../../utils/isValueRequired';
import {
  getNoRestrictionsDisplayValue,
  getValueRequiredPermissionDisplayValue,
} from '../../utils/getNoRestrictionsDisplayValue';
import { PERMISSION_LABELS } from '../../consts';

const getTheme = (selectedPermission?: SelectionPermission): SectionButtonTheme => {
  return selectedPermission ? SectionButtonTheme.SOLID : SectionButtonTheme.DASHED;
};

const getSelectedDisplay = (frameList?: {
  routeFrameCodes?: RouteFrameCode[];
  listFiles?: FileList[];
  mapRouteFrameCodes?: RouteFrameCode[];
}): CodeNameModel[] | undefined => {
  if (!frameList) return undefined;

  const { routeFrameCodes = [], listFiles = [], mapRouteFrameCodes = [] } = frameList;

  return generateRouteFramesSelectedDisplay(routeFrameCodes, mapRouteFrameCodes, listFiles);
};

const getSelectedProp = (
  selectedDisplay: CodeNameModel[] | undefined,
  selectedPermission?: SelectionPermission,
): SectionSelectedType | undefined => {
  if (selectedDisplay?.length) {
    return sortByCategoryIncludeExclude(selectedDisplay);
  }

  if (selectedPermission && isValueRequired(selectedPermission)) {
    return getValueRequiredPermissionDisplayValue(selectedPermission);
  }

  return getNoRestrictionsDisplayValue(selectedPermission);
};

const handleFrameListChange = ({
  frameList,
  dispatch,
  fieldName,
  newRouteFrameCodes,
}: {
  frameList: {
    value?: {
      routeFrameCodes?: RouteFrameCode[];
      listFiles?: FileList[];
      mapRouteFrameCodes?: RouteFrameCode[];
    };
    selectedPermission: SelectionPermission;
  };
  dispatch: Dispatch<AnyAction>;
  fieldName: ProductFieldName;
  newRouteFrameCodes: RouteFrameCode[];
}): void => {
  if (!frameList.selectedPermission) return;

  dispatch(
    changeNewProductField({
      fieldName,
      value: {
        routeFrameCodes: newRouteFrameCodes,
        listFiles: frameList.value?.listFiles || [],
      },
      selectedPermission: { code: frameList.selectedPermission, name: PERMISSION_LABELS[frameList.selectedPermission] },
    }),
  );
};

const handleFrameListChangeByInclude = ({
  frameList,
  included,
  dispatch,
  fieldName,
}: {
  frameList: {
    value?: {
      routeFrameCodes?: RouteFrameCode[];
      listFiles?: FileList[];
    };
    selectedPermission: SelectionPermission;
  };
  included?: boolean;
  dispatch: Dispatch<AnyAction>;
  fieldName: ProductFieldName;
}): void => {
  const newRouteFrameCodes = (frameList.value?.routeFrameCodes || []).filter(
    (value) => included === undefined || value.include !== included,
  );

  if (newRouteFrameCodes.length && !frameList.value?.listFiles) {
    dispatch(clearItemInNewProduct({ fieldName }));
    return;
  }

  dispatch(
    changeNewProductField({
      fieldName,
      value: {
        routeFrameCodes: newRouteFrameCodes,
        listFiles: frameList.value?.listFiles || [],
      },
      selectedPermission: {
        code: frameList.selectedPermission,
        name: PERMISSION_LABELS[frameList.selectedPermission],
      },
    }),
  );
};

const getNewListOfFiles = (listFiles: FileList[], routeFrameCodesSet: Set<string>, included?: boolean): FileList[] => {
  return listFiles.filter(
    (value) => !routeFrameCodesSet.has(value.name) && (included === undefined || value.include === included),
  );
};

const ProductFrameList: FC = () => {
  const dispatch = useDispatch();
  const isReadOnly = useSelector(getIsReadOnly);
  const mediaType = useSelector((state: Store) => state.productManagement.newProduct.mediaType?.value);
  const channels = useSelector((state: Store) => state.productManagement.newProduct.channels?.value);
  const secondaryPanelType = useSelector((state: Store) => state.productManagement.secondaryPanel);
  const frameList = useSelector((state: Store) => state.productManagement.newProduct.frameList);
  const selectedPermission = frameList?.selectionPermission;

  const { localeCode, currencyCode } = useSelector((state: Store) => state.publisher.configuration);
  const extraMenuActions = useFrameListExportActions({
    routeFrameCodes: frameList?.value?.routeFrameCodes,
    listFiles: frameList?.value?.listFiles,
    currencyCode,
    localeCode,
  });

  const onChangePanelVisibility = useOnChangePanelVisibility();
  const fieldName = ProductFieldName.FRAME_LIST;
  const isFrameListPanelOpen = secondaryPanelType === PlannerSecondaryPanelType.FRAME_LIST;

  const selectedDisplay = useMemo(() => getSelectedDisplay(frameList?.value), [frameList?.value]);

  const onRemoveRouteFrameCodeChip = (routeFrameCodesToRemove?: string[], included?: boolean): void => {
    if (!selectedPermission) return;

    if (!routeFrameCodesToRemove) {
      if (included !== undefined) {
        handleFrameListChangeByInclude({
          frameList: {
            value: frameList?.value,
            selectedPermission,
          },
          included,
          dispatch,
          fieldName,
        });
        return;
      }

      dispatch(clearItemInNewProduct({ fieldName }));
      return;
    }

    if (!routeFrameCodesToRemove.length) return;

    const routeFrameCodesSet = new Set(routeFrameCodesToRemove);
    const newListFiles = getNewListOfFiles(frameList.value?.listFiles || [], routeFrameCodesSet, included);
    const newRouteFrameCodes = (frameList.value?.routeFrameCodes || []).filter(
      (value) => included === undefined || value.include !== included,
    );

    if (!newListFiles.length && !newRouteFrameCodes.length) {
      dispatch(clearItemInNewProduct({ fieldName }));
      return;
    }

    if (routeFrameCodesSet.has('Frames')) {
      handleFrameListChange({
        newRouteFrameCodes,
        frameList: {
          value: frameList?.value,
          selectedPermission,
        },
        dispatch,
        fieldName,
      });

      return;
    }

    dispatch(
      changeNewProductField({
        fieldName,
        value: {
          routeFrameCodes: frameList.value?.routeFrameCodes || [],
          listFiles: getNewListOfFiles(frameList.value?.listFiles || [], routeFrameCodesSet, included),
        },
        selectedPermission: { code: selectedPermission, name: PERMISSION_LABELS[selectedPermission] },
      }),
    );
  };

  return (
    <>
      <SectionButton
        dataTestId="product-button-frame-list"
        label="Frame List"
        icon={routeFrameListSvg}
        isDisabled={!mediaType || !channels?.length}
        isEllipsisDisabled={isReadOnly}
        theme={getTheme(selectedPermission)}
        isActive={isFrameListPanelOpen}
        selected={getSelectedProp(selectedDisplay, selectedPermission)}
        onClick={() => onChangePanelVisibility(PlannerSecondaryPanelType.FRAME_LIST)}
        onClear={() => {
          dispatch(clearItemInNewProduct({ fieldName }));
          dispatch(hideSecondaryPanel());
        }}
        onRemoveChip={onRemoveRouteFrameCodeChip}
        extraMenuActions={extraMenuActions}
      />
      {isFrameListPanelOpen && (
        <ProductFrameListPanel onCancel={() => dispatch(hideSecondaryPanel())} isReadOnly={isReadOnly} />
      )}
    </>
  );
};

export default ProductFrameList;
